import { loadAssistant } from 'javascripts/async_modules';
import { createRoot, type Root } from 'react-dom/client';
import { createElement } from 'react';
import type { AssistantDock } from '../AssistantView/AssistantViewController';
import type { AgentParams } from '../../agents/Agent';
import { type TemplateCategory } from '../../../AiTemplatesModal/AiTemplateController';

let assistantInstance: Root | null = null;

type OpenAssistantOverride = (params: {
  agentName: string;
  agentParams: AgentParams;
}) => boolean;

let overrideHandlers: OpenAssistantOverride[] = [];

function registerAssistantOpenInterceptor(
  handler: OpenAssistantOverride
): () => void {
  overrideHandlers.push(handler);
  return () => {
    const index = overrideHandlers.indexOf(handler);
    if (index > -1) {
      overrideHandlers.splice(index, 1);
    }
  };
}

function clearOverrideHandlers() {
  overrideHandlers = [];
}

// Create an instance of the Assistant as a toolbar over the page. Loads the
// assistant code dynamically only when it is used. This is intended to be
// used from Rails views.
async function openAssistant({
  agentName,
  agentParams,
  dock = 'floating',
  focus = true,
  initialLibraryCategory,
  initialLibraryOpen = false,
}: {
  agentName: string;
  agentParams: AgentParams;
  dock?: AssistantDock;
  focus?: boolean;
  initialLibraryCategory?: TemplateCategory | 'Global_All';
  initialLibraryOpen?: boolean;
}) {
  // Check if any override handlers can handle this request
  for (const handler of overrideHandlers) {
    if (handler({ agentName, agentParams })) {
      return;
    }
  }
  let assistantDiv = document.getElementById('ai-assistant-view');

  if (assistantInstance && assistantDiv) {
    // TODO: ask the component to open and focus the prompt editor.
    const raft = assistantDiv.children[0] as HTMLDivElement;
    if (raft && raft.hasAttribute('popover')) {
      raft.hidePopover();
      raft.showPopover();
    }
  } else {
    // Load the assistant code and mount the component.
    const AssistantView = await loadAssistant();

    assistantDiv ||= document.createElement('div');
    assistantDiv.id = 'ai-assistant-view';
    assistantDiv.setAttribute('data-skip-drawer-close', '');

    document.body.appendChild(assistantDiv);
    assistantInstance = createRoot(assistantDiv);
    assistantInstance.render(
      createElement(AssistantView, {
        agentName,
        agentParams,
        dock,
        focus,
        initialLibraryCategory,
        initialLibraryOpen,
      })
    );
  }
}

// Close the open assistant.
function closeAssistant() {
  if (assistantInstance) {
    const oldInstance = assistantInstance;
    assistantInstance = null;
    oldInstance.unmount();
  }
}

// The assistant is closed when the page changes. So it reintializes on the new
// page with the approapriate agent and context.
if (process.env.NODE_ENV !== 'test') {
  $(document).on('page:before-change', () => {
    closeAssistant();
    clearOverrideHandlers();
  });
}

export default openAssistant;
export { closeAssistant, registerAssistantOpenInterceptor };
